import React, { useState } from 'react';
import { sendSuggestionEmail, validateEmailJSConfig } from '../services/emailService';

import { useI18n } from '../contexts/I18nContext';
import { Lightbulb, MailCheck, Palette, ChevronDown } from 'lucide-react';

export default function SuggestionsPage() {
  const { t } = useI18n();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    suggestionType: 'feature',
    subject: '',
    message: '',
    brand: '',
    priority: 'medium',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const validateForm = () => {
    const newErrors = {};
    
    // Nome - obrigatório, máximo 50 caracteres
    if (!formData.name.trim()) {
      newErrors.name = t('suggestions.errors.nameRequired');
    } else if (formData.name.length > 50) {
      newErrors.name = 'Name should be at most 50 characters';
    }
    
    // Email - obrigatório, máximo 100 caracteres, formato válido
    if (!formData.email.trim()) {
      newErrors.email = t('suggestions.errors.emailRequired');
    } else if (formData.email.length > 100) {
      newErrors.email = 'Email should be at most 100 characters';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('suggestions.errors.emailInvalid');
    }
    
    // Assunto - obrigatório, máximo 100 caracteres
    if (!formData.subject.trim()) {
      newErrors.subject = t('suggestions.errors.subjectRequired');
    } else if (formData.subject.length > 100) {
      newErrors.subject = 'Subject should be at most 100 characters';
    }
    
    // Mensagem - obrigatória, mínimo 10, máximo 500 caracteres
    if (!formData.message.trim()) {
      newErrors.message = t('suggestions.errors.messageRequired');
    } else if (formData.message.length < 10) {
      newErrors.message = t('suggestions.errors.messageMin');
    } else if (formData.message.length > 500) {
      newErrors.message = 'Message should be at most 500 characters';
    }
    
    // Marca sugerida - opcional, máximo 100 caracteres
    if (formData.brand && formData.brand.length > 100) {
      newErrors.brand = 'Brand should be at most 100 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Forbid input beyond max lengths and special characters if needed
    if (name === 'name' && value.length > 50) return;
    if (name === 'email' && value.length > 100) return;
    if (name === 'subject' && value.length > 100) return;
    if (name === 'message' && value.length > 500) return;
    if (name === 'brand' && value.length > 100) return;

    // Remove special characters from name and brand fields
    if (name === 'name' || name === 'brand') {
      const sanitizedValue = value.replace(/[^a-zA-Z0-9\s]/g, '');
      setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
      return;
    }

    // General case
    const sanitizedValue = value.replace(/[\u200B-\u200D\uFEFF]/g, ''); // Remove zero-width chars

    // Remove special characters from subject and message except basic punctuation
    if (name === 'subject' || name === 'message') {
      const allowedChars = /[^a-zA-Z0-9\s.,:!?'"()-]/g;
      const cleanedValue = sanitizedValue.replace(allowedChars, '');
      setFormData(prev => ({ ...prev, [name]: cleanedValue }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setFormData({
      name: '',
      email: '',
      suggestionType: 'feature',
      subject: '',
      message: '',
      brand: '',
      priority: 'medium'
    });
    setErrors({});
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    if (!validateEmailJSConfig()) {
      // fallback simulado
      setTimeout(() => {
        setIsSubmitting(false);
        setIsSubmitted(true);
        setTimeout(() => resetForm(), 3000);
      }, 1500);
      return;
    }

    try {
      const result = await sendSuggestionEmail(formData);
      if (result.success) {
        setIsSubmitted(true);
        setTimeout(() => resetForm(), 5000);
      } else {
        setSubmitError(result.message);
      }
    } catch (error) {
      setSubmitError(t('suggestions.errors.unexpected'));
      console.error('Submit error:', error);
    }
    setIsSubmitting(false);
  };

  if (isSubmitted) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="card-elegant p-8 text-center">
            <div className="w-20 h-20 bg-gradient-primary rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl text-white">✨</span>
            </div>
            <h2 className="text-2xl font-display font-bold">
              {t('suggestions.thanks.title')}
            </h2>
            <p className="text-elegant mb-6">
              {t('suggestions.thanks.body')}
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-rose-pastel-400 to-lavender-400 rounded-full mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
      <div className="max-w-4xl mx-auto space-y-8">
        
        {/* Header Section */}
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold heading-display mb-4 animate-pulse bg-gradient-to-r from-yellow-400 to-orange-500 mb-4">
            {t('suggestions.header.title')}
            <Lightbulb className="inline-block w-8 h-8 animate-pulse text-orange-400 ml-2 -mt-1" />
          </h2>
          <p className="text-base md:text-md text-elegant max-w-2xl mx-auto">
            {t('suggestions.header.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="card-elegant p-6 space-y-6">
              
              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="text-xl font-display font-semibold text-slate-700 mb-4">
                  {t('suggestions.form.info')}
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      {t('suggestions.form.name')} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      maxLength={50}
                      className={`input-elegant w-full ${errors.name ? 'border-red-300 focus:border-red-500' : ''}`}
                      placeholder={t('suggestions.form.namePH')}
                    />
                    {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                    <p className="mt-1 text-xs text-gray-500">{formData.name.length}/50</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      {t('suggestions.form.email')} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      maxLength={100}
                      className={`input-elegant w-full ${errors.email ? 'border-red-300 focus:border-red-500' : ''}`}
                      placeholder={t('suggestions.form.emailPH')}
                    />
                    {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                    <p className="mt-1 text-xs text-gray-500">{formData.email.length}/100</p>
                  </div>
                </div>
              </div>

              {/* Suggestion Details */}
              <div className="space-y-4">
                <h3 className="text-xl font-display font-semibold text-slate-700 mb-4">
                  {t('suggestions.form.details')}
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      {t('suggestions.form.type')}
                    </label>
                    <div className="relative">
                      <select
                        name="suggestionType"
                        value={formData.suggestionType}
                        onChange={handleInputChange}
                        className="input-elegant w-full appearance-none pr-10 font-medium cursor-pointer"
                      >
                        <option value="feature">{t('suggestions.types.feature')}</option>
                        <option value="improvement">{t('suggestions.types.improvement')}</option>
                        <option value="bug">{t('suggestions.types.bug')}</option>
                        <option value="brand">{t('suggestions.types.brand')}</option>
                        <option value="ui">{t('suggestions.types.ui')}</option>
                        <option value="other">{t('suggestions.types.other')}</option>
                      </select>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      {t('suggestions.form.priority')}
                    </label>
                    <div className="relative">
                      <select
                        name="priority"
                        value={formData.priority}
                        onChange={handleInputChange}
                        className="input-elegant w-full appearance-none pr-10 font-medium cursor-pointer"
                      >
                        <option value="low">{t('suggestions.priority.low')}</option>
                        <option value="medium">{t('suggestions.priority.medium')}</option>
                        <option value="high">{t('suggestions.priority.high')}</option>
                      </select>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  </div>
                </div>

                {formData.suggestionType === 'brand' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      {t('suggestions.form.brand')}
                    </label>
                    <input
                      type="text"
                      name="brand"
                      value={formData.brand}
                      onChange={handleInputChange}
                      maxLength={100}
                      className={`input-elegant w-full ${errors.brand ? 'border-red-300 focus:border-red-500' : ''}`}
                      placeholder={t('suggestions.form.brandPH')}
                    />
                    {errors.brand && <p className="mt-1 text-sm text-red-600">{errors.brand}</p>}
                    <p className="mt-1 text-xs text-gray-500">{formData.brand.length}/100</p>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">
                    {t('suggestions.form.subject')} *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    maxLength={100}
                    className={`input-elegant w-full ${errors.subject ? 'border-red-300 focus:border-red-500' : ''}`}
                    placeholder={t('suggestions.form.subjectPH')}
                  />
                  {errors.subject && <p className="mt-1 text-sm text-red-600">{errors.subject}</p>}
                  <p className="mt-1 text-xs text-gray-500">{formData.subject.length}/100</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">
                    {t('suggestions.form.message')} *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={6}
                    className={`input-elegant w-full resize-y ${errors.message ? 'border-red-300 focus:border-red-500' : ''}`}
                    placeholder={t('suggestions.form.messagePH')}
                  />
                  {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message}</p>}
                  <p className="mt-1 text-xs text-gray-500">{formData.message.length}/500</p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`btn btn-primary w-full py-4 text-lg font-semibold ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('suggestions.submit.sending')}
                    </span>
                  ) : (
                    t('suggestions.submit.send')
                  )}
                </button>
                
                {/* Error Message */}
                {submitError && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <span className="text-red-500 text-sm">⚠️</span>
                      <div className="text-sm text-red-700">
                        <p className="font-medium">{t('suggestions.submit.errorTitle')}</p>
                        <p className="mt-1">{submitError}</p>
                        <p className="mt-2 text-xs">
                          {t('suggestions.submit.altContact')} <strong>markersync@gmail.com</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* Sidebar Information */}
          <div className="space-y-6">
            {/* Guidelines */}
            <div className="card-elegant p-6">
              <h3 className="text-lg font-display font-semibold text-slate-700 mb-4">
                {t('suggestions.guidelines.title')}
              </h3>
              <div className="space-y-3 text-sm text-elegant">
                <div className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>{t('suggestions.guidelines.item1')}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>{t('suggestions.guidelines.item2')}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>{t('suggestions.guidelines.item3')}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>{t('suggestions.guidelines.item4')}</span>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="card-elegant p-6">
              <h3 className="text-lg font-display font-semibold text-slate-700 mb-4">
                {t('suggestions.contact.title')}
              </h3>
              <div className="space-y-3 text-sm text-elegant">
                <div className="flex items-center gap-2">
                  <MailCheck className="w-5 h-5 text-blue-500" />
                  <span>markersync@gmail.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-purple-500" />
                  <span>@markersync_ (TikTok)</span>
                </div>
              </div>
            </div>


          </div>
        </div>
      </div>
  );
}
